/**
 * frontend/src/pages/Dashboard.js
 * 
 * Main user dashboard for managing change requests within a workspace.
 * It handles workspace initialization, displaying stats, and filtering/searching change requests.
 * 
 * Common functions:
 * - Dashboard: Main component that orchestrates workspace loading and data display.
 * - loadWorkspaceData: Fetches all necessary data for the current workspace.
 * - getWorkspaceNameFromEmail: Helper to automatically suggest a workspace name for corporate emails.
 * - filteredRequests: Computed list of change requests based on search and status filters.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Row, Col } from 'react-bootstrap';
import { FiPlus, FiSearch, FiFileText, FiClock, FiEye, FiCheckCircle, FiZap } from 'react-icons/fi';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';
import ChangeRequestCard from '../components/ChangeRequest/ChangeRequestCard';
import WorkspaceModal from '../components/Workspace/WorkspaceModal';

// Personal email domains that require workspace name input
const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk',
  'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  'zoho.com',
  'mail.com',
  'yandex.com', 'yandex.ru',
  'gmx.com', 'gmx.net',
  'tutanota.com',
  'fastmail.com',
  'hey.com',
];

// Dashboard-specific styles (scoped to avoid affecting other pages)
const styles = {
  pageWrapper: {
    minHeight: '100vh',
    background: '#f8fafc',
  },
  mainContent: {
    marginLeft: '72px',
    marginTop: '64px',
    minHeight: 'calc(100vh - 64px)',
    background: '#f8fafc',
  },
  contentWrapper: {
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  welcomeSection: {
    marginBottom: '32px',
  },
  welcomeTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '4px',
  },
  welcomeSubtitle: {
    color: '#64748b',
    fontSize: '0.95rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  statCardActive: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    border: '2px solid #6366f1',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    fontSize: '1.25rem',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '2px',
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#64748b',
    fontWeight: '500',
  },
  searchSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '24px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '0.95rem',
    color: '#1e293b',
    background: 'transparent',
  },
  filterSelect: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '0.875rem',
    color: '#475569',
    background: 'white',
    minWidth: '140px',
  },
  resultCount: {
    color: '#64748b',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px',
  },
  emptyState: {
    background: 'white',
    borderRadius: '12px',
    padding: '60px 20px',
    textAlign: 'center',
    border: '1px solid #e2e8f0',
  },
  emptyIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    background: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontSize: '1.5rem',
    color: '#94a3b8',
  },
  emptyTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  emptyText: {
    color: '#64748b',
    marginBottom: '20px',
    fontSize: '0.9rem',
  },
  newButton: {
    background: '#6366f1',
    border: 'none',
    borderRadius: '10px',
    padding: '10px 20px',
    fontWeight: '600',
    fontSize: '0.9rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '60px auto',
  },
};

const statConfig = [
  { status: 'all', label: 'Total', icon: FiFileText, color: '#6366f1', bg: '#eef2ff' },
  { status: 'pending', label: 'Pending', icon: FiClock, color: '#f59e0b', bg: '#fef3c7' },
  { status: 'in_review', label: 'In Review', icon: FiEye, color: '#8b5cf6', bg: '#ede9fe' },
  { status: 'approved', label: 'Approved', icon: FiCheckCircle, color: '#10b981', bg: '#d1fae5' },
  { status: 'implemented', label: 'Implemented', icon: FiZap, color: '#06b6d4', bg: '#cffafe' },
];

// Helper function to extract workspace name from email domain
const getWorkspaceNameFromEmail = (email) => {
  if (!email) return null;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;

  // Check if it's a personal email domain
  if (PERSONAL_EMAIL_DOMAINS.includes(domain)) {
    return null; // Needs manual input
  }

  // Extract company name from domain (e.g., instagram.com -> Instagram)
  const companyName = domain.split('.')[0];
  // Capitalize first letter
  return companyName.charAt(0).toUpperCase() + companyName.slice(1);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentWorkspace,
    changeRequests,
    fetchWorkspaces,
    fetchWorkspace,
    fetchChangeRequests,
    createWorkspace,
  } = useWorkspace();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const initialized = useRef(false);

  const loadWorkspaceData = useCallback(async (workspaceId) => {
    setLoading(true);
    try {
      await Promise.all([
        fetchWorkspace(workspaceId),
        fetchChangeRequests(workspaceId),
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWorkspace, fetchChangeRequests]);

  // Initialize workspace on mount
  useEffect(() => {
    if (!user || initialized.current) return;
    initialized.current = true;

    const initializeWorkspace = async () => {
      setInitializing(true);
      try {
        // Fetch user's existing workspaces
        const existingWorkspaces = await fetchWorkspaces();

        if (existingWorkspaces && existingWorkspaces.length > 0) {
          // User has workspace(s), load the first one
          const workspaceId = existingWorkspaces[0].id;
          await loadWorkspaceData(workspaceId);
        } else {
          // No workspace exists, check email domain
          const autoWorkspaceName = getWorkspaceNameFromEmail(user?.email);

          if (autoWorkspaceName) {
            // Company email - auto-create workspace
            setWorkspaceLoading(true);
            const newWorkspace = await createWorkspace(autoWorkspaceName);
            await loadWorkspaceData(newWorkspace.id);
            setWorkspaceLoading(false);
          } else {
            // Personal email - show modal
            setShowWorkspaceModal(true);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Failed to initialize workspace:', error);
        setLoading(false);
      } finally {
        setInitializing(false);
      }
    };

    initializeWorkspace();
  }, [user, fetchWorkspaces, createWorkspace, loadWorkspaceData]);

  const handleWorkspaceCreate = async (name) => {
    setWorkspaceLoading(true);
    try {
      const newWorkspace = await createWorkspace(name);
      setShowWorkspaceModal(false);
      await loadWorkspaceData(newWorkspace.id);
    } catch (error) {
      console.error('Failed to create workspace:', error);
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const filteredRequests = changeRequests.filter((request) => {
    const matchesSearch =
      request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = changeRequests.reduce((acc, request) => {
    acc[request.status] = (acc[request.status] || 0) + 1;
    return acc;
  }, {});

  const getStatCount = (status) => {
    if (status === 'all') return changeRequests.length;
    return statusCounts[status] || 0;
  };

  // Show loading screen while initializing workspace
  if (initializing && !showWorkspaceModal) {
    return (
      <div className="loading-screen" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(255,255,255,0.1)',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
            {workspaceLoading ? 'Creating your workspace...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Show modal only (no dashboard content) when workspace needs to be created
  if (showWorkspaceModal) {
    return (
      <WorkspaceModal
        show={true}
        onSubmit={handleWorkspaceCreate}
        loading={workspaceLoading}
        userName={user?.name}
      />
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <Sidebar workspace={currentWorkspace} />
      <Header workspace={currentWorkspace} />

      <main style={styles.mainContent}>
        <div style={styles.contentWrapper}>
          {/* Welcome Section */}
          <div style={styles.welcomeSection}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h1 style={styles.welcomeTitle}>Change Requests</h1>
                <p style={styles.welcomeSubtitle}>
                  {currentWorkspace?.name || 'Workspace'}
                </p>
              </div>
              <Button style={styles.newButton} onClick={() => navigate('/dashboard/new')}>
                <FiPlus />
                New Request
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div style={styles.statsGrid}>
            {statConfig.map((stat) => {
              const Icon = stat.icon;
              const isActive = statusFilter === stat.status;
              return (
                <div
                  key={stat.status}
                  style={isActive ? styles.statCardActive : styles.statCard}
                  onClick={() => setStatusFilter(stat.status)}
                >
                  <div style={{ ...styles.statIcon, background: stat.bg, color: stat.color }}>
                    <Icon />
                  </div>
                  <div style={styles.statValue}>{getStatCount(stat.status)}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Search & Filter */}
          <div style={styles.searchSection}>
            <FiSearch style={{ color: '#94a3b8', fontSize: '1.1rem' }} />
            <input
              type="text"
              placeholder="Search change requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="implemented">Implemented</option>
            </select>
            <span style={styles.resultCount}>
              {filteredRequests.length} result{filteredRequests.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Change Requests */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={styles.loadingSpinner} />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>
                <FiFileText />
              </div>
              <h5 style={styles.emptyTitle}>No Change Requests Found</h5>
              <p style={styles.emptyText}>
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first change request to get started'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button style={styles.newButton} onClick={() => navigate('/dashboard/new')}>
                  <FiPlus />
                  New Request
                </Button>
              )}
            </div>
          ) : (
            <Row className="g-3">
              {filteredRequests.map((change) => (
                <Col key={change.id} md={12}>
                  <ChangeRequestCard change={change} />
                </Col>
              ))}
            </Row>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
